import { CSSProperties, ReactNode, Ref } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Box, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { CardActionsHeader } from '../CardActionsHeader';

// ── inline styles (no DS prop equivalent) ──────────────────────────────────
/** scrollable card area; fixed height + overflow + bespoke padding have no DS Box prop */
const scrollAreaStyle: CSSProperties = {
  width: '100%',
  marginTop: 16,
  height: 600,
  overflowX: 'hidden',
  overflowY: 'auto',
  paddingRight: 8,
  paddingLeft: 2,
  paddingBottom: 20,
};
/** header → cards spacing (12px); see CardActionsHeader for the removed -12 hack */
const outerColumnStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 12 };
/** card → card spacing (24px) */
const cardColumnStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 24 };

interface IHubCardListProps {
  /** Italic blurb shown above the list. */
  intro: ReactNode;
  /** Column labels for the CardActionsHeader (e.g. ['Block', 'Invite']). */
  headerActions: string[];
  /** Header right offset to align labels over the action buttons. */
  headerRightOffset?: number;
  /** Optional scroll-restore ref for the scrollable area. */
  scrollRef?: Ref<HTMLDivElement>;
  /** The mapped <ProfessionalCard /> items. */
  children: ReactNode;
}

/**
 * Shared layout for the redesign card lists (Favorites / Worked With / New to You).
 * Owns the intro, scroll area, action-header and the header→cards / card→card spacing so the
 * three lists don't each re-implement (and drift on) the same container. Spacing is structural
 * (outer gap 12, inner gap 24) — no negative-margin pull-up.
 */
export const HubCardList = ({
  intro,
  headerActions,
  headerRightOffset,
  scrollRef,
  children,
}: IHubCardListProps) => (
  <Box width="100%">
    <Typography italic color={colors.neutral['500']}>
      {intro}
    </Typography>

    <div ref={scrollRef} style={scrollAreaStyle}>
      <motion.div style={outerColumnStyle} layout>
        <CardActionsHeader actions={headerActions} isNewDesign rightOffset={headerRightOffset} />
        <div style={cardColumnStyle}>
          <AnimatePresence>{children}</AnimatePresence>
        </div>
      </motion.div>
    </div>
  </Box>
);
